import { Button, DatePicker, Empty, Form, Select, SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import CourseOption from '../../components/CourseOption';
import UserOption from '../../components/UserOption';
import SearchLoadingIndicator from '../../components/SearchLoadingIndicator';
import CourseAPI from '../../course/CourseAPI';
import Course from '../../course/types/Course';
import UserAPI from '../../user/UserAPI';
import { Permission } from '../../user/types/Permission';
import User, { UserRole } from '../../user/types/User';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import { getSearchParam, stringifyValues } from '../../utils/searchParams';
import ClientCompanyAPI from '../api/ClientCompanyAPI';
import InvoiceRecipientAPI from '../api/InvoiceRecipientAPI';
import { MinimalClientCompany } from '../types/ClientCompany';
import InvoiceRecipient from '../types/InvoiceRecipient';
import useDebounce from '../../utils/useDebounce';

import SubsidiaryCompany from '../../subsidiaryCompany/types/SubsidiaryCompany';
import SubsidiaryCompanyAPI from '../../subsidiaryCompany/SubsidiaryCompanyAPI';
import {
  BookingOrderFilterCategory,
  SearchParamKeys,
} from '../views/BookingOrders';
import { ReloadOutlined } from '@ant-design/icons';
import { getBookingOrderFilterFromSearchParams } from '../utils/bookingOrder';
import useInstructorOptions from '../../user/hooks/useInstructorOptions';

const { RangePicker } = DatePicker;

export enum InvoiceFilter {
  All = 'all',
  Invoiced = 'invoiced',
  NotInvoiced = 'notInvoiced',
}

type FilterFormValues = {
  subsidiaryCompanyIds?: number[];
  date?: [dayjs.Dayjs, dayjs.Dayjs];
  invoiceFilter: InvoiceFilter;
  courseIds?: number[];
  responsibleBookerIds?: (number | null)[];
  invoiceRecipientIds?: (number | null)[];
  instructorIds?: (number | null)[];
  clientCompanyIds?: number[];
};

function filterUserOption(input: string, option?: DefaultOptionType) {
  const entryName = option?.props.children.props.user
    ? option?.props.children.props.user.name
    : option?.props.children.props.noUserText;

  return entryName.toLowerCase().indexOf(input.toLowerCase()) >= 0;
}

function selectProps(
  placeholder: string,
  notFoundDescription?: string,
  loading = false,
  showSearch = true,
  disabled = false,
): Pick<
  SelectProps,
  | 'mode'
  | 'showSearch'
  | 'allowClear'
  | 'loading'
  | 'placeholder'
  | 'notFoundContent'
  | 'disabled'
> {
  return {
    mode: 'multiple',
    showSearch,
    allowClear: true,
    loading,
    disabled,
    placeholder,
    notFoundContent: notFoundDescription ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={notFoundDescription}
      />
    ) : undefined,
  };
}

type BookingOrderFilterProps = {
  className?: string;
  defaultSubsidiaryCompanyId?: number;
};

const BookingOrderFilterPicker: React.FC<BookingOrderFilterProps> = ({
  className,
  defaultSubsidiaryCompanyId,
}) => {
  const [currentUser] = useUser();
  const location = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [responsibleBookers, setResponsibleBookers] = useState<User[]>([]);
  const [loadingResponsibleBookers, setLoadingResponsibleBookers] =
    useState<boolean>(false);
  const [invoiceRecipients, setInvoiceRecipients] = useState<
    InvoiceRecipient[]
  >([]);
  const [clientCompanies, setClientCompanies] = useState<
    MinimalClientCompany[]
  >([]);
  const [subsidiaryCompanies, setSubsidiaryCompanies] = useState<
    SubsidiaryCompany[]
  >([]);
  const [loadingSubsidiaryCompanies, setLoadingSubsidiaryCompanies] =
    useState<boolean>(false);
  const [invoiceRecipientSearch, setInvoiceRecipientSearch] = useState('');
  const [clientCompanySearch, setClientCompanySearch] = useState('');
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();
  const firstUpdate = useRef(true);

  const selectedSubsidiaryCompanyIds = useMemo(() => {
    return getSearchParam(
      searchParams,
      SearchParamKeys.SubsidiaryCompanyIds,
      'list',
    )?.map(Number);
  }, [searchParams]);

  const {
    debounce: invoiceRecipientDebounce,
    loading: loadingInvoiceRecipientDebounce,
  } = useDebounce(
    (value: string) => {
      if (!currentUser) return;

      return InvoiceRecipientAPI.getInvoiceRecipients({
        name: value || undefined,
        subsidiaryCompanyIds: selectedSubsidiaryCompanyIds,
      });
    },
    (res) => setInvoiceRecipients(res.data.data),
    {
      beforeDebounce: () => setInvoiceRecipients([]),
      dependencies: [currentUser, selectedSubsidiaryCompanyIds],
    },
  );

  const {
    debounce: clientCompanyDebounce,
    loading: loadingClientCompanyDebounce,
  } = useDebounce(
    (value: string) => {
      if (!currentUser) return;

      return ClientCompanyAPI.getClientCompanies({
        name: value || undefined,
        subsidiaryCompanyIds: selectedSubsidiaryCompanyIds,
      });
    },
    (res) => setClientCompanies(res.data.data),
    {
      beforeDebounce: () => setClientCompanies([]),
      dependencies: [currentUser, selectedSubsidiaryCompanyIds],
    },
  );

  const urgentTabIsSelected = useMemo(() => {
    return (
      getSearchParam(searchParams, SearchParamKeys.Category, 'first') ===
      BookingOrderFilterCategory.Urgent
    );
  }, [searchParams]);

  const selectedInstructorIds = useMemo(
    () =>
      getBookingOrderFilterFromSearchParams(searchParams).instructorIds?.filter(
        (id): id is number => id != null,
      ),
    [searchParams],
  );

  const {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
  } = useInstructorOptions({
    enabled: !urgentTabIsSelected,
    hydrateSelectedIds: selectedInstructorIds,
  });

  const canFilterSubsidiaryCompanies = useMemo(() => {
    return (
      userHasPermission(currentUser, Permission.SUBSIDIARY_COMPANY_READ_LIST) &&
      userHasPermission(currentUser, Permission.BOOKING_ORDER_READ_GLOBAL)
    );
  }, [currentUser]);

  const canFilterInvoiceRecipients = useMemo(() => {
    return userHasPermission(
      currentUser,
      Permission.INVOICE_RECIPIENT_READ_LIST,
    );
  }, [currentUser]);

  const fetchCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const { data } = await CourseAPI.getCourses();
      setCourses(data);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  const fetchSubsidiaryCompanies = useCallback(async () => {
    if (!canFilterSubsidiaryCompanies) {
      return;
    }

    setLoadingSubsidiaryCompanies(true);
    try {
      const { data } = await SubsidiaryCompanyAPI.getSubsidiaryCompanies();
      setSubsidiaryCompanies(data);
    } finally {
      setLoadingSubsidiaryCompanies(false);
    }
  }, [canFilterSubsidiaryCompanies]);

  const fetchResponsibleBookers = useCallback(async () => {
    setLoadingResponsibleBookers(true);
    try {
      const { data } = await UserAPI.getUsers({
        roles: [UserRole.Admin, UserRole.Booker],
      });
      setResponsibleBookers(data);
    } finally {
      setLoadingResponsibleBookers(false);
    }
  }, []);

  const getInvoiceFilterFromIsInvoiced = useCallback(
    (isInvoiced: boolean | undefined) => {
      switch (isInvoiced) {
        case true:
          return InvoiceFilter.Invoiced;
        case false:
          return InvoiceFilter.NotInvoiced;
        default:
          return InvoiceFilter.All;
      }
    },
    [],
  );

  // Refetch subsidiary company dependent entities
  useEffect(() => {
    invoiceRecipientDebounce('');
    if (userHasPermission(currentUser, Permission.CLIENT_COMPANY_READ)) {
      clientCompanyDebounce('');
    }
  }, [
    currentUser,
    clientCompanyDebounce,
    invoiceRecipientDebounce,
    subsidiaryCompanies,
  ]);

  useEffect(() => {
    // Fetch entities on initial render
    if (firstUpdate.current) {
      fetchCourses();
      fetchResponsibleBookers();
      invoiceRecipientDebounce('');
      fetchSubsidiaryCompanies();
      if (userHasPermission(currentUser, Permission.CLIENT_COMPANY_READ)) {
        clientCompanyDebounce('');
      }
      firstUpdate.current = false;
      return;
    }
    invoiceRecipientDebounce(invoiceRecipientSearch);
  }, [
    invoiceRecipientSearch,
    invoiceRecipientDebounce,
    fetchCourses,
    fetchResponsibleBookers,
    fetchSubsidiaryCompanies,
    clientCompanyDebounce,
    currentUser,
  ]);

  useEffect(() => {
    if (firstUpdate.current) {
      return;
    }
    clientCompanyDebounce(clientCompanySearch);
  }, [clientCompanySearch, clientCompanyDebounce]);

  useEffect(() => {
    const from = getSearchParam(searchParams, SearchParamKeys.From, 'moment');
    const to = getSearchParam(searchParams, SearchParamKeys.To, 'moment');

    const bookingOrderFilter =
      getBookingOrderFilterFromSearchParams(searchParams);

    form.setFieldsValue({
      ...bookingOrderFilter,
      date: from && to && [from, to],
      instructorIds: bookingOrderFilter.instructorIds?.map((id) =>
        id === null ? 0 : id,
      ),
      responsibleBookerIds: bookingOrderFilter.responsibleBookerIds?.map(
        (id) => (id === null ? 0 : id),
      ),
      invoiceRecipientIds: bookingOrderFilter.invoiceRecipientIds?.map((id) =>
        id === null ? 0 : id,
      ),
      invoiceFilter: getInvoiceFilterFromIsInvoiced(
        bookingOrderFilter.isInvoiced,
      ),
    });
  }, [
    setSearchParams,
    getInvoiceFilterFromIsInvoiced,
    location.search,
    searchParams,
    subsidiaryCompanies,
    form,
  ]);

  const getIsInvoicedFromInvoiceFilter = useCallback(
    (invoiceFilter: InvoiceFilter) => {
      switch (invoiceFilter) {
        case InvoiceFilter.Invoiced:
          return true;
        case InvoiceFilter.NotInvoiced:
          return false;
        case InvoiceFilter.All:
        default:
          return undefined;
      }
    },
    [],
  );

  const onFilterValuesChange = useCallback(
    (_changedValues: FilterFormValues, values: FilterFormValues) => {
      if (!currentUser) {
        return;
      }

      const from = values.date?.[0].startOf('day').toISOString();
      const to = values.date?.[1].endOf('day').toISOString();

      const instructorIds = values.instructorIds?.includes(0)
        ? values.instructorIds.filter((id) => id !== 0).concat([null])
        : values.instructorIds;

      const responsibleBookerIds = values.responsibleBookerIds?.includes(0)
        ? values.responsibleBookerIds.filter((id) => id !== 0).concat([null])
        : values.responsibleBookerIds;

      const invoiceRecipientIds = values.invoiceRecipientIds?.includes(0)
        ? values.invoiceRecipientIds.filter((id) => id !== 0).concat([null])
        : values.invoiceRecipientIds;

      const isInvoiced = getIsInvoicedFromInvoiceFilter(values.invoiceFilter);

      setSearchParams((prev) =>
        stringifyValues({
          category: prev.get(SearchParamKeys.Category),
          statuses: prev.get(SearchParamKeys.Statuses) ?? undefined,
          subsidiaryCompanyIds: values.subsidiaryCompanyIds,
          from,
          to,
          courseIds: values.courseIds,
          instructorIds,
          responsibleBookerIds,
          invoiceRecipientIds,
          isInvoiced,
          clientCompanyIds: values.clientCompanyIds,
        }),
      );
    },
    [currentUser, getIsInvoicedFromInvoiceFilter, setSearchParams],
  );

  const resetFilter = useCallback(() => {
    setSearchParams((prev) => {
      const category = prev.get(SearchParamKeys.Category);
      const isUrgent = category === BookingOrderFilterCategory.Urgent;

      return stringifyValues({
        category: category,
        statuses: prev.get(SearchParamKeys.Statuses) ?? undefined,
        subsidiaryCompanyIds: defaultSubsidiaryCompanyId
          ? [defaultSubsidiaryCompanyId]
          : [],
        from: isUrgent ? dayjs().startOf('day').toISOString() : undefined,
        to: isUrgent ? dayjs().add(30, 'days').toISOString() : undefined,
      });
    });
  }, [defaultSubsidiaryCompanyId, setSearchParams]);

  return (
    <div className={`${className}`}>
      <div className="bg-white rounded-md p-3">
        <Form
          form={form}
          onValuesChange={onFilterValuesChange}
          layout="vertical"
          className="-mb-3">
          <Form.Item name="date" label={t('common.date')}>
            <RangePicker disabled={urgentTabIsSelected} />
          </Form.Item>
          {canFilterSubsidiaryCompanies && (
            <Form.Item
              name="subsidiaryCompanyIds"
              label={t('common.subsidiaryCompanies')}>
              <Select
                {...selectProps(
                  t('components.BookingOrderFilterPicker.selectCompanies'),
                  t('components.BookingOrderFilterPicker.noCompaniesFound'),
                  loadingSubsidiaryCompanies,
                  true,
                )}
                filterOption={(input, option) =>
                  option?.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }>
                {subsidiaryCompanies?.map(
                  (subsidiaryCompany) =>
                    subsidiaryCompany.id && (
                      <Select.Option
                        key={subsidiaryCompany.id}
                        value={subsidiaryCompany.id}>
                        {subsidiaryCompany.name}
                      </Select.Option>
                    ),
                )}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="clientCompanyIds" label={t('common.companies')}>
            <Select
              {...selectProps(
                t('components.BookingOrderFilterPicker.selectCompanies'),
                t('components.BookingOrderFilterPicker.noCompaniesFound'),
                loadingClientCompanyDebounce,
                true,
                !userHasPermission(
                  currentUser,
                  Permission.CLIENT_COMPANY_READ,
                ) || urgentTabIsSelected,
              )}
              onSearch={setClientCompanySearch}
              onBlur={() => setClientCompanySearch('')}
              filterOption={false}
              notFoundContent={
                loadingClientCompanyDebounce ? <SearchLoadingIndicator /> : null
              }>
              {clientCompanies?.map(
                (clientCompany) =>
                  clientCompany.id && (
                    <Select.Option
                      key={clientCompany.id}
                      value={clientCompany.id}>
                      {clientCompany.name}
                    </Select.Option>
                  ),
              )}
            </Select>
          </Form.Item>
          <Form.Item name="instructorIds" label={t('common.instructors')}>
            <Select
              {...selectProps(
                t('components.BookingOrderFilterPicker.selectInstructors'),
                t('components.BookingOrderFilterPicker.noInstructorsFound'),
                loadingInstructors,
                true,
                urgentTabIsSelected,
              )}
              filterOption={false}
              notFoundContent={
                loadingInstructorOptions ? (
                  <SearchLoadingIndicator />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t(
                      'components.BookingOrderFilterPicker.noInstructorsFound',
                    )}
                  />
                )
              }
              onDropdownVisibleChange={(open) => {
                if (open) {
                  loadFirstInstructorPage('');
                }
              }}
              onPopupScroll={(event) => {
                const target = event.target as HTMLDivElement;
                const isNearBottom =
                  target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 32;

                if (isNearBottom && hasMoreInstructors) {
                  loadMoreInstructors();
                }
              }}
              onSearch={searchInstructors}>
              <Select.Option key={0} value={0}>
                <UserOption
                  user={undefined}
                  noUserText={t(
                    'components.BookingOrderFilterPicker.noInstructor',
                  )}
                />
              </Select.Option>
              {instructors?.map(
                (instructor) =>
                  instructor.id && (
                    <Select.Option key={instructor.id} value={instructor.id}>
                      <UserOption user={instructor} currentUser={currentUser} />
                    </Select.Option>
                  ),
              )}
            </Select>
          </Form.Item>
          <Form.Item name="courseIds" label={t('common.courses')}>
            <Select
              {...selectProps(
                t('components.BookingOrderFilterPicker.selectCourses'),
                t('components.BookingOrderFilterPicker.noCoursesFound'),
                loadingCourses,
                true,
                urgentTabIsSelected,
              )}
              filterOption={(input, option) =>
                option?.props.children.props.course.name
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }>
              {courses?.map((course) => (
                <Select.Option key={course.id} value={course.id}>
                  <CourseOption user={currentUser} course={course} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="responsibleBookerIds"
            label={t('common.responsibleBooker')}>
            <Select
              {...selectProps(
                t(
                  'components.BookingOrderFilterPicker.selectResponsibleBookers',
                ),
                t(
                  'components.BookingOrderFilterPicker.noResponsibleBookersFound',
                ),
                loadingResponsibleBookers,
              )}
              filterOption={filterUserOption}>
              <Select.Option key={0} value={0}>
                <UserOption
                  user={undefined}
                  noUserText={t(
                    'components.BookingOrderFilterPicker.noResponsibleBooker',
                  )}
                />
              </Select.Option>
              {responsibleBookers?.map(
                (responsibleBooker) =>
                  responsibleBooker.id && (
                    <Select.Option
                      key={responsibleBooker.id}
                      value={responsibleBooker.id}>
                      <UserOption user={responsibleBooker} />
                    </Select.Option>
                  ),
              )}
            </Select>
          </Form.Item>
          {canFilterInvoiceRecipients && (
            <Form.Item
              name="invoiceRecipientIds"
              label={t('common.invoiceRecipient')}>
              <Select
                {...selectProps(
                  t(
                    'components.BookingOrderFilterPicker.selectInvoiceRecipient',
                  ),
                  t(
                    'components.BookingOrderFilterPicker.noInvoiceRecipientsFound',
                  ),
                  loadingInvoiceRecipientDebounce,
                  true,
                  urgentTabIsSelected,
                )}
                onSearch={setInvoiceRecipientSearch}
                onBlur={() => setInvoiceRecipientSearch('')}
                filterOption={false}
                notFoundContent={
                  loadingInvoiceRecipientDebounce ? (
                    <SearchLoadingIndicator />
                  ) : null
                }>
                {!loadingInvoiceRecipientDebounce && (
                  <Select.Option key={0} value={0}>
                    {t(
                      'components.BookingOrderFilterPicker.noInvoiceRecipient',
                    )}
                  </Select.Option>
                )}
                {invoiceRecipients?.map((invoiceRecipient) => (
                  <Select.Option
                    key={invoiceRecipient.id}
                    value={invoiceRecipient.id}>
                    {invoiceRecipient.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label={t('components.BookingOrderFilterPicker.invoiceStatus')}
            name="invoiceFilter"
            valuePropName="checked">
            <Select
              disabled={urgentTabIsSelected}
              value={form.getFieldValue('invoiceFilter')}>
              <Select.Option value={InvoiceFilter.All}>
                {t('components.BookingOrderFilterPicker.all')}
              </Select.Option>
              <Select.Option value={InvoiceFilter.Invoiced}>
                {t('components.BookingOrderFilterPicker.invoiced')}
              </Select.Option>
              <Select.Option value={InvoiceFilter.NotInvoiced}>
                {t('components.BookingOrderFilterPicker.notInvoiced')}
              </Select.Option>
            </Select>
          </Form.Item>
          <Button
            className="w-full flex items-center mb-3"
            type="primary"
            onClick={resetFilter}>
            <ReloadOutlined />
            <div>{t('components.BookingOrderFilterPicker.resetFilter')}</div>
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default BookingOrderFilterPicker;
